import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { motion } from 'motion/react';
import { CheckCircle2, Loader2, XCircle, Coins, ArrowRight, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { verifyStripeSession } from '../utils/api';
import { toast } from 'sonner';

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { credits, refreshCredits } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [creditsAdded, setCreditsAdded] = useState(0);
  const [newBalance, setNewBalance] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [returnPath, setReturnPath] = useState('/');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const returnPathParam = searchParams.get('return');

    console.log('[PAYMENT SUCCESS] Session ID:', sessionId);
    console.log('[PAYMENT SUCCESS] Return path param:', returnPathParam);

    // Set return path from URL parameter
    if (returnPathParam) {
      setReturnPath(returnPathParam);
      console.log('[PAYMENT SUCCESS] Setting return path to:', returnPathParam);
    } else {
      console.log('[PAYMENT SUCCESS] No return path, defaulting to /');
    }

    if (!sessionId) {
      setStatus('error');
      setErrorMessage('No payment session found');
      return;
    }

    verifyPayment(sessionId);
  }, [searchParams]);

  const verifyPayment = async (sessionId: string) => {
    try {
      const result = await verifyStripeSession(sessionId);
      console.log('[PAYMENT SUCCESS] Verification result:', result);

      if (result.success) {
        // Refresh credits from server
        await refreshCredits();

        setCreditsAdded(result.credits);
        setNewBalance(result.newBalance);
        setStatus('success');

        // No toast - success page already shows the message
      } else {
        setStatus('error');
        setErrorMessage(`Payment not completed. Status: ${result.status}`);
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to verify payment');
    }
  };

  const handleContinue = () => {
    navigate(returnPath, { replace: true });
  };

  const handleBackToDashboard = () => {
    navigate('/', { replace: true });
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl border-2 border-slate-200 shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="flex justify-center mb-6">
            <Loader2 className="w-16 h-16 text-[#4F819F] animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            Verifying Payment
          </h1>
          <p className="text-slate-600">
            Please wait while we confirm your payment...
          </p>
        </motion.div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl border-2 border-slate-200 shadow-xl p-8 max-w-md w-full"
        >
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3 text-center">
            Payment Verification Failed
          </h1>
          <p className="text-slate-600 mb-8 text-center">
            {errorMessage || 'We couldn\'t verify your payment. Please contact support.'}
          </p>
          
          <div className="space-y-3">
            <button
              onClick={handleBackToDashboard}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-medium rounded-xl hover:from-[#456070] hover:to-[#4F819F] transition-all shadow-lg hover:shadow-xl"
            >
              Go to Dashboard
            </button>
            <button
              onClick={handleContinue}
              className="w-full px-6 py-3 border-2 border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors"
            >
              Try Again
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Success Card - Match Login/Signup Style */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border-2 border-slate-200 shadow-xl p-8"
        >
          {/* Success Icon with Animation */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="flex justify-center mb-6"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20" />
              <div className="relative w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle2 className="w-12 h-12 text-white" />
              </div>
            </div>
          </motion.div>

          {/* Success Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center mb-6"
          >
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Payment Successful!
            </h1>
            <p className="text-slate-600">
              Your credits have been added to your account
            </p>
          </motion.div>

          {/* Credits Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-[#4F819F]/10 to-green-50 rounded-xl p-6 mb-8 border-2 border-[#4F819F]/20"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-slate-700">
                <Coins className="w-5 h-5 text-[#4F819F]" />
                <span className="font-medium">Credits Added</span>
              </div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: 'spring' }}
                className="text-3xl font-bold text-green-600"
              >
                +{creditsAdded}
              </motion.div>
            </div>

            <div className="h-px bg-slate-200 my-4" />

            <div className="flex items-center justify-between">
              <span className="text-slate-600">Your New Balance</span>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6, type: 'spring' }}
                className="text-2xl font-bold text-[#4F819F]"
              >
                {newBalance} credits
              </motion.div>
            </div>
          </motion.div>

          {/* Invoice Email Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3"
          >
            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-blue-900 font-medium mb-1">📧 Invoice Sent to Your Email</p>
              <p className="text-sm text-blue-700">
                A professional invoice has been automatically sent to your email. You can also access it anytime from the Billing page.
              </p>
            </div>
          </motion.div>

          {/* Context-Aware Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="space-y-3"
          >
            {/* If from NewAnalysis page - show "Continue Analysis" as primary action */}
            {returnPath === '/new' ? (
              <>
                <button
                  onClick={() => navigate('/new?autostart=true', { replace: true })}
                  className="w-full px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-medium rounded-xl hover:from-[#456070] hover:to-[#4F819F] transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <ArrowRight className="w-5 h-5" />
                  Continue Analysis
                </button>
                <button
                  onClick={handleBackToDashboard}
                  className="w-full px-6 py-3 border-2 border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                  Back to Dashboard
                </button>
                <button
                  onClick={() => navigate('/billing', { replace: true })}
                  className="w-full px-6 py-3 border-2 border-blue-300 bg-blue-50 text-blue-700 font-medium rounded-xl hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  View Invoice
                </button>
              </>
            ) : (
              /* If from Dashboard or other pages - just go back to dashboard */
              <>
                <button
                  onClick={handleBackToDashboard}
                  className="w-full px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-medium rounded-xl hover:from-[#456070] hover:to-[#4F819F] transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <ArrowRight className="w-5 h-5" />
                  Back to Dashboard
                </button>
                <button
                  onClick={() => navigate('/billing', { replace: true })}
                  className="w-full px-6 py-3 border-2 border-blue-300 bg-blue-50 text-blue-700 font-medium rounded-xl hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  View Invoice
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}